/**
 * scoreNarrative.ts — deterministic "why this grade" sentence for the RHS Opportunity-Score card.
 *
 * A translate-don't-transplant port of the reference prototype's getTractInsight (a 5-band string
 * template). This is our OWN implementation — no import from the prototype — with the locked
 * product decisions for T-162 / BEAD-0070:
 *   (a) bands on the DISPLAYED score (the composite headline number), not tract.lviScore, so the
 *       narrative band always matches the tier word shown beside it (BEAD-0067 / Ricardo option A);
 *   (c) the "positive/negative balance" qualifier and the competitive-pressure level are derived
 *       from the actual factor values, so the words never contradict the printed number;
 *   (d) the competitive-pressure clause is dropped when CMPI is null or a SUSPECT 0.00
 *       (cmpi === 0 while stations exist — the NJ data pattern under investigation in BEAD-0066);
 *       a genuine 0.00 with no stations is kept (it truthfully means "no competition").
 *
 * Pure + deterministic: no LLM, no network, no I/O. All inputs already ship on CensusTract via
 * /api/tracts/at and /api/tracts/bbox — this module adds zero API/pipeline/DB surface.
 */
import type { CensusTract } from './tractModel'
import { dominantTypeLabel } from './tractModel'

/** LVI tier thresholds — mirror ScorePanel scoreColor/TIER_WORDS (D-12). Descending. */
export const NARRATIVE_TIERS = { prime: 70, strong: 60, moderate: 45, marginal: 35 } as const

/** Near-zero band for the gap qualifier — |gap| <= this reads as "balanced". */
const GAP_NEUTRAL_EPS = 0.05

function fmt1(n: number): string {
  return n.toFixed(1)
}

/** Gap phrase whose qualifier follows the sign of the value (decision c). */
function gapClause(gap: number): string {
  const v = fmt1(gap)
  if (gap > GAP_NEUTRAL_EPS) return `a positive demand potential vs. supply balance (${v})`
  if (gap < -GAP_NEUTRAL_EPS) return `a negative demand potential vs. supply balance (${v})`
  return `a balanced demand potential vs. supply position (${v})`
}

/**
 * Whether the CMPI value is trustworthy enough to cite (decision d).
 * Suspect zero: cmpi === 0 while the tract has existing stations — pressure cannot truly be 0
 * where stations exist (BEAD-0066). A genuine 0 with no stations IS citable ("no competition").
 */
function cmpiCitable(
  cmpi: number | null | undefined,
  stationCount: number | null | undefined,
): boolean {
  if (cmpi == null || Number.isNaN(cmpi)) return false
  if (cmpi === 0 && (stationCount ?? 0) > 0) return false
  return true
}

/** Competitive-pressure level word, derived from the value (decision c — never hard-coded). */
function pressureLevel(cmpi: number): string {
  if (cmpi < 25) return 'low'
  if (cmpi < 50) return 'moderate'
  return 'elevated'
}

interface Stations {
  has: boolean
  n: number
  word: string
}
function stationInfo(count: number | null | undefined): Stations {
  const n = count ?? 0
  return { has: count != null, n, word: n === 1 ? 'station' : 'stations' }
}

/**
 * Build the one-line narrative for a tract at the given displayed score.
 *
 * @param tract        the resolved census tract (factor values are tract-level)
 * @param displayScore the number shown in the headline (composite site score, or tract LVI in State A)
 */
export function getScoreNarrative(tract: CensusTract, displayScore: number): string {
  const gap = gapClause(tract.gapScore)
  const st = stationInfo(tract.stationCount)
  const cmpiOk = cmpiCitable(tract.cmpiScore, tract.stationCount)
  const pressure = cmpiOk
    ? `${pressureLevel(tract.cmpiScore)} competitive pressure (${fmt1(tract.cmpiScore)})`
    : ''
  const typeLabel = dominantTypeLabel(tract.dominantType)

  // Prime (>= 70)
  if (displayScore >= NARRATIVE_TIERS.prime) {
    const tail = cmpiOk ? ` and ${pressure}` : ''
    return `Strong overall investment opportunity, with ${gap}${tail} — a prime location for new deployment.`
  }

  // Strong (>= 60)
  if (displayScore >= NARRATIVE_TIERS.strong) {
    const stationSuffix = st.has ? ` (${st.n} existing ${st.word})` : ''
    return `Solid opportunity${stationSuffix}, with ${gap}. Good business case for new infrastructure.`
  }

  // Moderate (>= 45)
  if (displayScore >= NARRATIVE_TIERS.moderate) {
    const stationSuffix = st.has ? ` and ${st.n} ${st.word} already serving the tract` : ''
    return `Moderate opportunity in this ${typeLabel} area, with ${gap}${stationSuffix}.`
  }

  // Marginal (>= 35) — headwinds framing; prefer CMPI when citable, else lean on stations/gap.
  if (displayScore >= NARRATIVE_TIERS.marginal) {
    if (cmpiOk && st.has) {
      return `Marginal opportunity: ${pressure} and ${st.n} existing ${st.word} create headwinds for new entrants.`
    }
    if (cmpiOk) {
      return `Marginal opportunity: ${pressure} creates headwinds for new entrants.`
    }
    if (st.has) {
      return `Marginal opportunity: ${st.n} existing ${st.word} and ${gap} create headwinds for new entrants.`
    }
    return `Marginal opportunity, with ${gap} — headwinds for new entrants.`
  }

  // Unfavorable (< 35) — level-conditional (no hard-coded "heavy saturation" that could contradict CMPI).
  if (cmpiOk) {
    return `Unfavorable: ${pressure} with ${gap}. Not recommended for new deployment.`
  }
  return `Unfavorable, with ${gap}. Not recommended for new deployment.`
}
